// features.js - Handles actionable features in the chat interface

import { apiUrl } from './modules/api-settings.js';

// Fonction utilitaire pour calculer la largeur du texte
function getTextWidth(text, font) {
  const canvas = getTextWidth.canvas || (getTextWidth.canvas = document.createElement("canvas"));
  const context = canvas.getContext("2d");
  context.font = font;
  const metrics = context.measureText(text);
  return metrics.width;
}

class FeatureSystem {
  constructor() {
    this.features = [];
    this.suggestionsElement = null;
    this.store = null; // Initialize as null
    this.createNewConversation = null; // Initialize as null
    this.initializeSuggestionsDOM(); // Renamed to avoid conflict with new initialize method
    this.registerFeatures();
    this.ensureWalletTimestamps(); // Assurer que tous les wallets ont un timestamp
  }

  // Method to inject dependencies after store is initialized
  initialize(storeInstance, createNewConversationFn) {
    if (!storeInstance || typeof createNewConversationFn !== 'function') {
      console.error("FeatureSystem initialization failed: Invalid store or createNewConversation function provided.");
      return;
    }
    this.store = storeInstance;
    this.createNewConversation = createNewConversationFn;
  }

  // Nouvelle méthode pour activer une fonctionnalité à partir d'un bouton externe
  activateFeatureFromButton(featureName) {
    // Trouver la fonctionnalité par son nom
    const feature = this.features.find(f => f.name === featureName);
    
    if (!feature) {
      console.error(`Feature "${featureName}" not found`);
      return;
    }
    
    // Utiliser le premier trigger de la fonctionnalité
    const trigger = feature.trigger[0];
    
    // Vérifier si cette feature nécessite un formulaire spécial (wallet)
    if ((feature.name.includes('wallet') && 
        (feature.name.includes('add') || feature.name.includes('change') || feature.name.includes('scan_specific'))) ||
        feature.name === 'scan_specific_wallet_number') {
      // Si c'est une feature qui nécessite un formulaire, l'afficher
      this.hideSuggestions();
      this.showWalletForm(feature);
    } else {
      // Sinon, simuler la saisie du trigger et l'envoi
      const messageInput = document.getElementById('messageInput');
      const sendBtn = document.getElementById('sendBtn');

      if (messageInput && sendBtn) {
        // Définir la valeur du champ de saisie
        messageInput.value = trigger;
        
        // Déclencher l'événement input pour activer le bouton d'envoi
        const inputEvent = new Event('input', { bubbles: true });
        messageInput.dispatchEvent(inputEvent);
        
        // Court délai pour permettre la mise à jour de l'interface
        setTimeout(() => {
          // Cliquer programmatiquement sur le bouton d'envoi
          sendBtn.click();
        }, 50);
      } else {
        console.error("Could not find message input or send button");
      }
    }
  }

  // Initialize the suggestions element (DOM related)
  initializeSuggestionsDOM() {
    // Create a suggestions element and append it to the input container
    this.suggestionsElement = document.createElement('div');
    this.suggestionsElement.className = 'feature-suggestions';
    this.suggestionsElement.style.display = 'none';
    
    // Add to DOM after message input
    document.addEventListener('DOMContentLoaded', () => {
      const inputContainer = document.querySelector('.input-wrapper');
      if (inputContainer) {
        // Insert suggestions before the input itself for better layout
        const messageInput = document.getElementById('messageInput');
        if (messageInput && messageInput.parentNode === inputContainer) {
           inputContainer.insertBefore(this.suggestionsElement, messageInput);
        } else {
           // Fallback if structure is different
           inputContainer.appendChild(this.suggestionsElement);
        }
        
        // Attach input event to message input
        if (messageInput) {
          messageInput.addEventListener('input', this.handleInputChange.bind(this));
          messageInput.addEventListener('keydown', this.handleKeyDown.bind(this));
          
          // Ajouter un gestionnaire global pour le formulaire de chat
          const chatForm = messageInput.closest('form');
          if (chatForm) {
            chatForm.addEventListener('submit', () => {
              // Nettoyer les gestionnaires lors de la soumission du formulaire
              setTimeout(() => {
                this.cleanupInputHandlers();
              }, 0);
            });
          }
        }
        
        // Set up send button listener to hide suggestions
        this.setupSendButtonListener();
      } else {
         console.warn("Feature suggestions: Input container not found.");
      }
    });
  }

  // Register all available features
  registerFeatures() {
    // Define the wallet features with triggers, descriptions, and handler functions
    this.features = [
      {
        name: 'scan_my_wallet',
        trigger: ['scan my wallet', 'scan wallet'],
        description: 'Scan your registered wallet',
        exampleFormat: 'scan my wallet',
        permissionLevel: 'free',
        handler: this.scanMyWallet.bind(this)
      },
      {
        name: 'add_my_wallet',
        trigger: ['add my wallet', 'add wallet', 'register wallet'],
        description: 'Register a new wallet',
        exampleFormat: 'add wallet 0x123ABC... on ETH',
        permissionLevel: 'free',
        handler: this.addWallet.bind(this)
      },
      {
        name: 'change_my_wallet',
        trigger: ['change my wallet', 'change wallet', 'update wallet'],
        description: 'Change your registered wallet',
        exampleFormat: 'change wallet 1BTC... on BTC',
        permissionLevel: 'free',
        handler: this.changeWallet.bind(this)
      },
      {
        name: 'scan_specific_wallet_number',
        trigger: ['scan specific wallet', 'scan wallet number'],
        description: 'Scan a specific registered wallet by number',
        exampleFormat: 'scan specific wallet 1',
        permissionLevel: 'lane',
        handler: this.scanMyWalletNumber.bind(this)
      },
      {
        name: 'scan_my_wallets',
        trigger: ['scan my wallets', 'scan all wallets'],
        description: 'Scan all your registered wallets',
        exampleFormat: 'scan my wallets',
        permissionLevel: 'lane',
        handler: this.scanAllWallets.bind(this)
      },
      {
        name: 'scan_specific_wallet',
        trigger: ['scan the wallet', 'scan wallet address'],
        description: 'Scan any wallet address',
        exampleFormat: 'scan the wallet bc1q... on BTC',
        permissionLevel: 'premium',
        handler: this.scanSpecificWallet.bind(this)
      }
    ];
  }

  // Get user's LANE token count (placeholder - replace with actual check via store or wallet connection)
  getUserLaneTokens() {
    // Check if we have a recent cache (less than 5 minutes old)
    const lastFetchTime = localStorage.getItem('laneTokensLastFetch');
    const currentTime = Date.now();
    const fiveMinutesInMs = 1 * 60 * 1000;
    
    // If we have a valid cache that's less than 5 minutes old, use it
    if (lastFetchTime && (currentTime - parseInt(lastFetchTime)) < fiveMinutesInMs) {
      return localStorage.getItem('laneTokens') || 0;
    }
    
    // Otherwise, fetch the latest token balance
    
    // Start an async fetch but return the current value immediately
    this.fetchUserLaneTokens();
    
    // Return current value while fetch is in progress
    return localStorage.getItem('laneTokens') || 0;
  }
  
  // Fetch user's LANE tokens from the API
  async fetchUserLaneTokens() {
    try {
      // Get current auth token
      const authToken = localStorage.getItem("quantumAuthToken");
      if (!authToken) {
        console.warn("Authentication token not found, can't fetch LANE tokens");
        return;
      }
      
      // Fetch user credits (which includes token balance)
      const response = await fetch(`${apiUrl}/user/credits`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch user credits: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Store the token balance in localStorage
      if (data.tokenBalance !== undefined) {
        localStorage.setItem('laneTokens', data.tokenBalance.toString());
        // Update last fetch timestamp
        localStorage.setItem('laneTokensLastFetch', Date.now().toString());
      }
    } catch (error) {
      console.error("Error fetching LANE tokens:", error);
    }
  }

  // Check user permission level based on LANE tokens
  getUserPermissionLevel() {
    const tokens = parseInt(this.getUserLaneTokens());
    if (tokens >= 35000) return 'premium';
    if (tokens > 0) return 'lane';
    return 'free';
  }

  // Get maximum allowed wallets based on LANE tokens
  getMaxWallets() {
    const tokens = parseInt(this.getUserLaneTokens());
    if (tokens >= 35000) return Infinity; // Unlimited for premium users
    if (tokens > 0) return 2 + Math.floor(tokens / 5000);
    console.log('tokens', tokens);
    return 1; // Free users get 1 wallet
  }

  // Handle input changes to show feature suggestions
  handleInputChange(event) {
    const inputText = event.target.value.toLowerCase().trim();
    
    // Si nous sommes en mode formulaire spécial, ne pas afficher les suggestions
    if (event.target.dataset.featureCommand === 'true') {
      this.hideSuggestions();
      return;
    }
    
    if (inputText.length < 3) {
      this.hideSuggestions();
      return;
    }
    
    const userPermission = this.getUserPermissionLevel();
    const matchingFeatures = this.features.filter(feature => {
      // Check if feature is available for user's permission level
      const permissionCheck = this.checkPermission(feature.permissionLevel, userPermission);
      if (!permissionCheck) return false;
      
      // Check if input matches any trigger
      return feature.trigger.some(trigger => 
        trigger.toLowerCase().includes(inputText) || inputText.includes(trigger.toLowerCase())
      );
    });
    
    if (matchingFeatures.length > 0) {
      this.showSuggestions(matchingFeatures);
    } else {
      this.hideSuggestions();
    }
  }

  // Show feature suggestions
  showSuggestions(features) {
    this.suggestionsElement.innerHTML = '';
    
    features.forEach(feature => {
      const suggestion = document.createElement('div');
      suggestion.className = 'feature-suggestion';
      
      // Use example format if available, otherwise use description
      if (feature.exampleFormat) {
        // Create a container for both description and example
        const descriptionDiv = document.createElement('div');
        descriptionDiv.className = 'feature-description';
        descriptionDiv.textContent = feature.description;
        
        const exampleDiv = document.createElement('div');
        exampleDiv.className = 'feature-example';
        exampleDiv.textContent = `Example: ${feature.exampleFormat}`;
        exampleDiv.style.fontSize = '0.85em';
        exampleDiv.style.opacity = '0.8';
        exampleDiv.style.marginTop = '2px';
        
        suggestion.appendChild(descriptionDiv);
        suggestion.appendChild(exampleDiv);
      } else {
        suggestion.textContent = feature.description;
      }
      
      suggestion.dataset.feature = feature.name;
      suggestion.dataset.trigger = feature.trigger[0]; // Use first trigger for replacement
      
      suggestion.addEventListener('click', () => {
        // Toujours fermer les suggestions après le clic
        this.hideSuggestions();
        
        // Check if this feature needs wallet address and network
        if (feature.name.includes('wallet') && (feature.name.includes('add') || feature.name.includes('change') || feature.name.includes('scan_specific'))) {
          this.showWalletForm(feature);
        } else {
          // Default behavior for other features: Populate input AND click send button
          const messageInput = document.getElementById('messageInput');
          const sendBtn = document.getElementById('sendBtn');

          if (messageInput && sendBtn) {
              // Utiliser seulement le trigger, pas l'exemple complet
              messageInput.value = feature.trigger[0];
              
              // Trigger input event first to potentially enable send button
              const inputEvent = new Event('input', { bubbles: true });
              messageInput.dispatchEvent(inputEvent);
              
              // Short delay to allow UI to potentially update (e.g., button enabling)
              setTimeout(() => {
                // Programmatically click the send button
                sendBtn.click();
              }, 50); // 50ms delay, adjust if needed

          } else {
             console.error("Could not find message input or send button for auto-submit.");
             // Fallback: just focus if submission fails
             if(messageInput) {
                 messageInput.value = feature.trigger[0]; 
                 messageInput.focus();
                 const inputEvent = new Event('input', { bubbles: true });
                 messageInput.dispatchEvent(inputEvent);
             }
          }
        }
      });
      
      this.suggestionsElement.appendChild(suggestion);
    });
    
    this.suggestionsElement.style.display = 'block';
  }

  // Create and show wallet form
  showWalletForm(feature) {
    // Hide suggestions
    this.hideSuggestions();
    
    const messageInput = document.getElementById('messageInput');
    if (!messageInput) return;
    
    // Déterminer le préfixe de commande en fonction de la feature
    let commandPrefix = '';
    let templatePattern = '';
    let templateType = 'address_network'; // Default
    
    if (feature.name === 'add_my_wallet') {
      commandPrefix = 'add wallet ';
      templatePattern = `${commandPrefix}_________________ on ______`;
    } else if (feature.name === 'change_my_wallet') {
      commandPrefix = 'change wallet ';
      const wallets = this.getStoredWallets();
      
      if (wallets.length === 0) {
        // Si pas de wallets, afficher un message et sortir
        const { showNotification } = window;
        if (typeof showNotification === 'function') {
          showNotification("Aucun wallet enregistré à modifier", "warning");
        }
        return;
      }
      
      templatePattern = `${commandPrefix}__ TO _________________ on ______`;
      templateType = 'change_wallet'; // Marqueur spécifique pour change wallet
      setTimeout(() => { this.showWalletNumberDropdown(wallets); }, 50);
      
    } else if (feature.name === 'scan_specific_wallet_number') {
      commandPrefix = feature.trigger[0] + ' ';
      const wallets = this.getStoredWallets();
      
      if (wallets.length === 0) {
        // Si pas de wallets, afficher un message et sortir
        const { showNotification } = window;
        if (typeof showNotification === 'function') {
          showNotification("Aucun wallet enregistré à scanner", "warning");
        }
        return;
      }
      
      templatePattern = `${commandPrefix}__`;
      templateType = 'number_only'; // Marqueur pour scan specific wallet number
      setTimeout(() => { this.showWalletNumberDropdown(wallets); }, 50);
      
    } else if (feature.name === 'scan_specific_wallet') {
      commandPrefix = 'scan the wallet ';
      templatePattern = `${commandPrefix}_________________ on ______`;
    }
    
    messageInput.value = templatePattern;
    
    // Placer le curseur au bon endroit selon la feature
    let cursorPosition = commandPrefix.length;
    if (feature.name === 'change_my_wallet') {
      // Pour change wallet, placer le curseur au niveau du numéro
      cursorPosition = commandPrefix.length; // Juste après "change wallet "
      messageInput.setSelectionRange(cursorPosition, cursorPosition + 2); // Sélectionner "__"
    } else {
      // Pour les autres, placer au début de l'adresse
      messageInput.setSelectionRange(cursorPosition, cursorPosition + 17);
    }
    messageInput.focus();
    // Ensure the input is visible after focus, especially on mobile
    messageInput.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    
    // État pour suivre les parties du template
    messageInput.dataset.addressPlaceholder = '_________________';
    messageInput.dataset.networkPlaceholder = '______';
    messageInput.dataset.commandPrefix = commandPrefix;
    messageInput.dataset.templateType = templateType; // Stocker le type de template
    if (templateType === 'change_wallet') {
      messageInput.dataset.numberPlaceholder = '__';
      messageInput.dataset.hasToSeparator = 'true';
    }
    
    // Fonction pour remplacer les placeholders quand l'utilisateur tape
    const handleInput = (e) => {
      const input = e.target;
      // Defensive check: Only run if in feature command mode
      if (input.dataset.featureCommand !== 'true') {
        // Optionally remove the listener if it shouldn't be active
        // input.removeEventListener('input', handleInput);
        return; 
      }
      
      const currentValue = input.value;
      const commandPrefix = input.dataset.commandPrefix;
      const addressPlaceholder = input.dataset.addressPlaceholder;
      const networkPlaceholder = input.dataset.networkPlaceholder;
      const templateType = input.dataset.templateType;
      const hasToSeparator = input.dataset.hasToSeparator === 'true';
      const numberPlaceholder = input.dataset.numberPlaceholder;
      
      // Détecter si l'utilisateur a tout effacé (sélectionner tout puis supprimer)
      if (currentValue === '' || currentValue.length === 0) {
        // Nettoyer les gestionnaires et données
        this.cleanupInputHandlers();
        
        // Garder l'input vide
        input.value = '';
        
        // Focus sur l'input
        input.focus();
        
        return;
      }
      
      // Vérifier la position du curseur et afficher le dropdown approprié
      const toIndex = hasToSeparator ? currentValue.indexOf(' TO ') : -1;
      const onIndex = currentValue.indexOf(' on ');
      
      // Si ' on ' a été supprimé et que ce n'est PAS un template number_only
      if (onIndex === -1 && templateType !== 'number_only') {
        // Restaurer le template avec ' on '
        if (templateType === 'change_wallet' && toIndex !== -1) {
          input.value = `${currentValue.substring(0, toIndex + 4)}${input.value.substring(toIndex + 4)} on ${networkPlaceholder}`;
        } else if (templateType === 'address_network'){
          // Restauration standard pour les templates avec adresse et réseau
          input.value = `${commandPrefix}${input.value.substring(commandPrefix.length)} on ${networkPlaceholder}`;
        }
        // Remettre le curseur sur ' on '
        const restoredOnIndex = input.value.indexOf(' on ');
        if(restoredOnIndex !== -1){
            input.setSelectionRange(restoredOnIndex, restoredOnIndex);
        }
        return;
      }
      
      // Gestion spéciale pour change wallet avec TO
      if (hasToSeparator) {
        if (toIndex === -1) {
          // Si ' TO ' a été supprimé, restaurer le template
          const beforeOn = currentValue.substring(0, onIndex);
          input.value = `${commandPrefix}${numberPlaceholder} TO ${addressPlaceholder} on ${currentValue.substring(onIndex + 4)}`;
          input.setSelectionRange(commandPrefix.length, commandPrefix.length + 2);
          return;
        }
        
        // Vérifier où se trouve le curseur
        if (input.selectionStart < toIndex) {
          // Curseur dans la partie numéro
          const wallets = this.getStoredWallets();
          if (wallets.length > 0) {
            this.showWalletNumberDropdown(wallets);
          }
        } else if (input.selectionStart > onIndex + 4) {
          // Curseur dans la partie réseau
          this.showNetworkDropdown(feature);
        }
      } else {
        // Gestion standard pour les autres features
        if (input.selectionStart > onIndex + 4) {
          // Afficher le menu déroulant des réseaux
          this.showNetworkDropdown(feature);
        } else {
          // Cacher le menu déroulant s'il existe
          const dropdown = document.getElementById('networkDropdown');
          if (dropdown) dropdown.remove();
        }
      }
    };
    
    // Gérer les touches spéciales
    const handleKeyDown = (e) => {
      const hasToSeparator = messageInput.dataset.hasToSeparator === 'true';
      
      // Si Escape est pressé, annuler le mode feature et nettoyer l'input
      if (e.key === 'Escape') {
        e.preventDefault();
        
        // Vider l'input
        messageInput.value = '';
        
        // Nettoyer les gestionnaires et données
        this.cleanupInputHandlers();
        
        // Focus sur l'input
        messageInput.focus();
        
        // Afficher une notification
        const { showNotification } = window;
        if (typeof showNotification === 'function') {
          showNotification("Commande annulée", "info");
        }
        
        return;
      }
      
      // Si Tab est pressé
      if (e.key === 'Tab') {
        e.preventDefault();
        
        if (hasToSeparator) {
          // Pour change wallet, naviguer entre numéro, adresse et réseau
          const toIndex = messageInput.value.indexOf(' TO ');
          const onIndex = messageInput.value.indexOf(' on ');
          
          if (toIndex !== -1 && onIndex !== -1) {
            // Déterminer où se trouve le curseur
            if (messageInput.selectionStart <= toIndex) {
              // Passer à l'adresse
              messageInput.setSelectionRange(toIndex + 4, toIndex + 4 + messageInput.dataset.addressPlaceholder.length);
            } else if (messageInput.selectionStart < onIndex) {
              // Passer au réseau
              messageInput.setSelectionRange(onIndex + 4, onIndex + 4 + messageInput.dataset.networkPlaceholder.length);
              this.showNetworkDropdown(feature);
            } else {
              // Revenir au numéro
              messageInput.setSelectionRange(commandPrefix.length, commandPrefix.length + 2);
              const wallets = this.getStoredWallets();
              if (wallets.length > 0) {
                this.showWalletNumberDropdown(wallets);
              }
            }
          }
        } else {
          // Navigation standard pour les autres features
          const onIndex = messageInput.value.indexOf(' on ') + 4;
          messageInput.setSelectionRange(onIndex, onIndex + messageInput.dataset.networkPlaceholder.length);
          this.showNetworkDropdown(feature);
        }
      }
      
      // Si Enter est pressé, vérifier si le formulaire est complet avant d'envoyer
      if (e.key === 'Enter' && !e.shiftKey) {
        const currentValue = messageInput.value;
        const addressPlaceholder = messageInput.dataset.addressPlaceholder;
        const networkPlaceholder = messageInput.dataset.networkPlaceholder;
        const numberPlaceholder = messageInput.dataset.numberPlaceholder;
        const hasToSeparator = messageInput.dataset.hasToSeparator === 'true';
        
        // Si la commande contient encore des placeholders, empêcher l'envoi
        if (currentValue.includes(addressPlaceholder) || 
            currentValue.includes(networkPlaceholder) || 
            currentValue.includes('__') ||
            (hasToSeparator && currentValue.includes(numberPlaceholder))) {
          e.preventDefault(); // Empêcher l'envoi du formulaire
          e.stopPropagation(); // Empêcher la propagation de l'événement
          
          // Afficher une notification
          const { showNotification } = window;
          if (typeof showNotification === 'function') {
            showNotification("Please complete all information before sending", "warning");
          }
          
          // Mettre en surbrillance le premier placeholder non rempli
          if (currentValue.includes('__')) {
            const placeholderPos = currentValue.indexOf('__');
            messageInput.setSelectionRange(placeholderPos, placeholderPos + 2);
            
            // Pour scan specific wallet, afficher le dropdown des wallets
            if (currentValue.startsWith('scan specific wallet') || currentValue.startsWith('scan wallet number')) {
              const wallets = this.getStoredWallets();
              if (wallets.length > 0) {
                this.showWalletNumberDropdown(wallets);
              }
            }
          } else if (hasToSeparator && currentValue.includes(numberPlaceholder)) {
            const placeholderPos = currentValue.indexOf(numberPlaceholder);
            messageInput.setSelectionRange(placeholderPos, placeholderPos + numberPlaceholder.length);
            
            // Afficher le dropdown des wallets
            const wallets = this.getStoredWallets();
            if (wallets.length > 0) {
              this.showWalletNumberDropdown(wallets);
            }
          } else if (currentValue.includes(addressPlaceholder)) {
            const placeholderPos = currentValue.indexOf(addressPlaceholder);
            messageInput.setSelectionRange(placeholderPos, placeholderPos + addressPlaceholder.length);
          } else if (currentValue.includes(networkPlaceholder)) {
            const placeholderPos = currentValue.indexOf(networkPlaceholder);
            messageInput.setSelectionRange(placeholderPos, placeholderPos + networkPlaceholder.length);
            this.showNetworkDropdown(feature);
          }
          return;
        }
      }
    };
    
    // Supprimer les anciens gestionnaires s'ils existent
    if (messageInput.dataset.hasInputHandler === 'true') {
      messageInput.removeEventListener('input', messageInput._inputHandler);
      messageInput.removeEventListener('keydown', messageInput._keyDownHandler);
    }
    
    // Stocker les références aux gestionnaires
    messageInput._inputHandler = handleInput;
    messageInput._keyDownHandler = handleKeyDown;
    messageInput.dataset.hasInputHandler = 'true';
    
    // Ajouter les gestionnaires d'événements
    messageInput.addEventListener('input', handleInput);
    messageInput.addEventListener('keydown', handleKeyDown);
    messageInput.addEventListener('click', handleInput);
    
    // Marquer l'input comme étant en mode commande
    messageInput.dataset.featureCommand = 'true';
  }
  
  // Nouvelle méthode pour afficher un dropdown avec les numéros de wallet
  showWalletNumberDropdown(wallets) {
    // Supprimer un éventuel menu déroulant existant
    const existingDropdown = document.getElementById('walletNumberDropdown');
    if (existingDropdown) existingDropdown.remove();
    
    const messageInput = document.getElementById('messageInput');
    if (!messageInput) return;
    
    // Créer le menu déroulant
    const dropdown = document.createElement('div');
    dropdown.id = 'walletNumberDropdown';
    dropdown.className = 'network-dropdown'; // Utiliser la même classe que le dropdown réseau
    
    // Ajouter les options au menu déroulant
    wallets.forEach((wallet, index) => {
      const option = document.createElement('div');
      option.className = 'network-option'; // Utiliser la même classe que le dropdown réseau
      
      // Formatter l'adresse pour afficher début et fin
      const address = wallet.address;
      const displayAddress = address.length > 16 
        ? `${address.substring(0, 8)}...${address.substring(address.length - 8)}`
        : address;
      
      option.textContent = `${index + 1} - ${wallet.network}: ${displayAddress}`;
      option.dataset.value = index + 1;
      option.dataset.address = wallet.address;
      option.dataset.network = wallet.network;
      
      option.addEventListener('click', () => {
        const currentValue = messageInput.value;
        const toIndex = currentValue.indexOf(' TO ');
        const commandPrefix = messageInput.dataset.commandPrefix;
        const walletNum = index + 1;

        
        // Identifier tous les dropdowns potentiels
        const dropdowns = document.querySelectorAll('#walletNumberDropdown, .network-dropdown');

        // Récupérer les fonctions de nettoyage définies dans showWalletNumberDropdown
        const closeHandler = window.currentCloseDropdownHandler; 
        const keydownHandler = window.currentDropdownKeydownHandler;

        if (toIndex !== -1 && commandPrefix === 'change wallet ') {
          messageInput.value = `${commandPrefix}${walletNum} TO ${messageInput.dataset.addressPlaceholder} on ${messageInput.dataset.networkPlaceholder}`;
          const newToIndex = messageInput.value.indexOf(' TO ');
          messageInput.setSelectionRange(newToIndex + 4, newToIndex + 4 + messageInput.dataset.addressPlaceholder.length);
        } else if (commandPrefix === 'scan specific wallet ' || commandPrefix === 'scan wallet number '){
          messageInput.value = `${commandPrefix}${walletNum}`;
          // Placer le curseur à la fin
          messageInput.setSelectionRange(messageInput.value.length, messageInput.value.length);
        } else {
           // Fallback? Ou juste fermer le dropdown?
        }
        
        // Supprimer immédiatement tous les dropdowns
        dropdowns.forEach(d => {
          if (d && d.parentNode) d.parentNode.removeChild(d);
        });
        
        // Nettoyer les écouteurs globaux immédiatement
        if (closeHandler) document.removeEventListener('click', closeHandler);
        if (keydownHandler) document.removeEventListener('keydown', keydownHandler);
        window.currentCloseDropdownHandler = null;
        window.currentDropdownKeydownHandler = null;
        
        // Focus sur l'input
        messageInput.focus();
        // Ensure the input is visible after refocusing, especially on mobile
        messageInput.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        
        // Déclencher l'événement input pour activer le bouton d'envoi
        const inputEvent = new Event('input', { bubbles: true });
        messageInput.dispatchEvent(inputEvent);
        
        // Afficher une notification de confirmation
        const { showNotification } = window;
        if (typeof showNotification === 'function') {
          showNotification(`Wallet ${walletNum} sélectionné`, "info");
        }
      });
      
      dropdown.appendChild(option);
    });
    
    // Positionner le menu déroulant
    const inputRect = messageInput.getBoundingClientRect();
    const inputContainer = messageInput.closest('.input-wrapper');
    if (!inputContainer) return;
    
    // Ajouter le menu au DOM
    inputContainer.appendChild(dropdown);
    
    // Positionner le menu comme le dropdown réseau
    dropdown.style.position = 'absolute';
    dropdown.style.top = `-${dropdown.offsetHeight + 10}px`;

    // Gérer le clic en dehors du menu pour le fermer
    const closeDropdownOnClickOutside = (event) => {
      if (!dropdown.contains(event.target) && event.target !== messageInput) {
        dropdown.remove();
        document.removeEventListener('click', closeDropdownOnClickOutside);
        document.removeEventListener('keydown', handleDropdownKeyDown);
        window.currentCloseDropdownHandler = null;
        window.currentDropdownKeydownHandler = null;
      }
    };
    
    // Permettre la navigation au clavier dans le menu
    const handleDropdownKeyDown = (e) => {
      const options = dropdown.querySelectorAll('.network-option');
      const selected = dropdown.querySelector('.network-option.selected');
      
      if (options.length === 0) return;
      
      // Touche flèche bas
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (!selected) {
          options[0].classList.add('selected');
        } else {
          const currentIndex = Array.from(options).indexOf(selected);
          selected.classList.remove('selected');
          const nextIndex = (currentIndex + 1) % options.length;
          options[nextIndex].classList.add('selected');
        }
      }
      
      // Touche flèche haut
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (!selected) {
          options[options.length - 1].classList.add('selected');
        } else {
          const currentIndex = Array.from(options).indexOf(selected);
          selected.classList.remove('selected');
          const nextIndex = (currentIndex - 1 + options.length) % options.length;
          options[nextIndex].classList.add('selected');
        }
      }
      
      // Touche Entrée ou Espace
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (selected) {
          // Simuler un clic sur l'option sélectionnée
          selected.click();
        }
      }
      
      // Touche Échap
      if (e.key === 'Escape') {
        e.preventDefault();
        dropdown.remove();
        document.removeEventListener('keydown', handleDropdownKeyDown);
        document.removeEventListener('click', closeDropdownOnClickOutside);
        window.currentCloseDropdownHandler = null;
        window.currentDropdownKeydownHandler = null;
      }
    };

    // Stocker les handlers pour pouvoir les supprimer correctement
    window.currentCloseDropdownHandler = closeDropdownOnClickOutside;
    window.currentDropdownKeydownHandler = handleDropdownKeyDown;
    
    // Ajouter l'écouteur d'événement pour fermer le menu (après un court délai)
    setTimeout(() => {
       document.addEventListener('click', closeDropdownOnClickOutside);
    }, 50); 
    
    // Ajouter l'écouteur d'événement pour la navigation clavier
    document.addEventListener('keydown', handleDropdownKeyDown);
  }

  // Afficher un menu déroulant pour sélectionner le réseau
  showNetworkDropdown(feature) {
    // Supprimer un éventuel menu déroulant existant
    const existingDropdown = document.getElementById('networkDropdown');
    if (existingDropdown) existingDropdown.remove();
    
    const messageInput = document.getElementById('messageInput');
    if (!messageInput) return;
    
    // Créer le menu déroulant
    const dropdown = document.createElement('div');
    dropdown.id = 'networkDropdown';
    dropdown.className = 'network-dropdown';
    
    // Définir les réseaux disponibles
    const networks = [
      { value: 'bitcoin', text: 'Bitcoin (BTC)' },
      // { value: 'ethereum', text: 'Ethereum (ETH)' },
      // { value: 'litecoin', text: 'Litecoin (LTC)' },
      // { value: 'bitcoin_cash', text: 'Bitcoin Cash (BCH)' },
      // { value: 'dogecoin', text: 'Dogecoin (DOGE)' }
    ];
    
    // Ajouter les options au menu déroulant
    networks.forEach(network => {
      const option = document.createElement('div');
      option.className = 'network-option';
      option.textContent = network.text;
      option.dataset.value = network.value;
      
      option.addEventListener('click', () => {
        // Extraire le préfixe de commande et l'adresse
        const currentValue = messageInput.value;
        const onIndex = currentValue.indexOf(' on ');
        
        if (onIndex !== -1) {
          const beforeOn = currentValue.substring(0, onIndex);
          
          // Identifier tous les dropdowns potentiels
          const dropdowns = document.querySelectorAll('#networkDropdown, .network-dropdown');
          
          // Construire la nouvelle valeur avec le réseau sélectionné
          messageInput.value = `${beforeOn} on ${network.value}`;
          
          // Supprimer immédiatement tous les dropdowns
          dropdowns.forEach(d => {
            if (d && d.parentNode) d.parentNode.removeChild(d);
          });
          
          // Nettoyer les écouteurs globaux immédiatement
          document.removeEventListener('click', closeDropdownOnClickOutside);
          document.removeEventListener('keydown', handleDropdownKeyDown);
          
          // Forcer la suppression de nouveau après un bref délai (doublement sécurisé)
          setTimeout(() => {
            const lateDropdowns = document.querySelectorAll('#networkDropdown, .network-dropdown');
            lateDropdowns.forEach(d => {
              if (d && d.parentNode) d.parentNode.removeChild(d);
            });
          }, 50);
          
          // Placer le curseur à la fin
          messageInput.setSelectionRange(messageInput.value.length, messageInput.value.length);
          
          // Focus sur l'input
          messageInput.focus();
          // Ensure the input is visible after refocusing, especially on mobile
          messageInput.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          
          // Déclencher l'événement input pour activer le bouton d'envoi
          const inputEvent = new Event('input', { bubbles: true });
          messageInput.dispatchEvent(inputEvent);
          
          // Afficher une notification de confirmation
          const { showNotification } = window;
          if (typeof showNotification === 'function') {
            showNotification(`Réseau sélectionné: ${network.text}`, "info");
          }
        }
      });
      
      dropdown.appendChild(option);
    });
    
    // Positionner le menu déroulant
    const inputRect = messageInput.getBoundingClientRect();
    const inputContainer = messageInput.closest('.input-wrapper');
    if (!inputContainer) return;
    
    // Ajouter le menu au DOM
    inputContainer.appendChild(dropdown);
    
    // Positionner le menu sous l'input
    const onIndex = messageInput.value.indexOf(' on ');
    dropdown.style.position = 'absolute';
    dropdown.style.top = `-${dropdown.offsetHeight + 10}px`;
    
    // Calculer la position horizontale
    if (onIndex !== -1) {
      const beforeText = messageInput.value.substring(0, onIndex + 4); // +4 pour inclure " on "
      const textWidth = getTextWidth(beforeText, getComputedStyle(messageInput).font);
      dropdown.style.left = `${textWidth}px`;
    }
    
    // Ajouter un écouteur d'événement global pour fermer le menu en cliquant ailleurs
    const closeDropdownOnClickOutside = (event) => {
      // Si on clique à l'extérieur du menu et de l'input
      if (!dropdown.contains(event.target) && event.target !== messageInput) {
        dropdown.remove();
        document.removeEventListener('click', closeDropdownOnClickOutside);
      }
    };
    
    // Ajouter l'écouteur d'événement après un court délai pour éviter qu'il se déclenche immédiatement
    setTimeout(() => {
      document.addEventListener('click', closeDropdownOnClickOutside);
    }, 100);
    
    // Ajouter une class au premier élément pour faciliter la navigation clavier
    const firstOption = dropdown.querySelector('.network-option');
    if (firstOption) firstOption.classList.add('selected');
    
    // Permettre la navigation au clavier dans le menu
    const handleDropdownKeyDown = (e) => {
      const options = dropdown.querySelectorAll('.network-option');
      const selected = dropdown.querySelector('.network-option.selected');
      
      if (options.length === 0) return;
      
      // Touche flèche bas
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (!selected) {
          options[0].classList.add('selected');
        } else {
          const currentIndex = Array.from(options).indexOf(selected);
          selected.classList.remove('selected');
          const nextIndex = (currentIndex + 1) % options.length;
          options[nextIndex].classList.add('selected');
        }
      }
      
      // Touche flèche haut
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (!selected) {
          options[options.length - 1].classList.add('selected');
        } else {
          const currentIndex = Array.from(options).indexOf(selected);
          selected.classList.remove('selected');
          const nextIndex = (currentIndex - 1 + options.length) % options.length;
          options[nextIndex].classList.add('selected');
        }
      }
      
      // Touche Entrée ou Espace
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (selected) {
          // Simuler un clic sur l'option sélectionnée
          selected.click();
        }
      }
      
      // Touche Échap
      if (e.key === 'Escape') {
        e.preventDefault();
        dropdown.remove();
        document.removeEventListener('keydown', handleDropdownKeyDown);
        document.removeEventListener('click', closeDropdownOnClickOutside);
      }
    };    
    // Ajouter l'écouteur d'événement pour la navigation clavier
    document.addEventListener('keydown', handleDropdownKeyDown);
    
    // Nettoyer les écouteurs d'événements quand le dropdown est supprimé
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.removedNodes) {
          mutation.removedNodes.forEach((node) => {
            if (node === dropdown) {
              document.removeEventListener('keydown', handleDropdownKeyDown);
              document.removeEventListener('click', closeDropdownOnClickOutside);
              observer.disconnect();
            }
          });
        }
      });
    });
    
    observer.observe(inputContainer, { childList: true });
  }
  
  // Nettoyer les gestionnaires d'événements
  cleanupInputHandlers() {
    const messageInput = document.getElementById('messageInput');
    if (!messageInput) return;
    
    // Supprimer les gestionnaires d'événements
    if (messageInput.dataset.hasInputHandler === 'true') {
      messageInput.removeEventListener('input', messageInput._inputHandler);
      messageInput.removeEventListener('keydown', messageInput._keyDownHandler);
      messageInput.removeEventListener('click', messageInput._inputHandler);
    }
    
    // Supprimer les données
    delete messageInput.dataset.hasInputHandler;
    delete messageInput.dataset.featureCommand;
    delete messageInput.dataset.addressPlaceholder;
    delete messageInput.dataset.networkPlaceholder;
    delete messageInput.dataset.commandPrefix;
    delete messageInput.dataset.numberPlaceholder;
    delete messageInput.dataset.hasToSeparator;
    
    // Supprimer tous les dropdowns potentiels
    const dropdowns = document.querySelectorAll('#networkDropdown, .network-dropdown');
    dropdowns.forEach(dropdown => {
      if (dropdown && dropdown.parentNode) dropdown.parentNode.removeChild(dropdown);
    });

    // Nettoyer les écouteurs globaux potentiellement actifs
    // Utiliser les références stockées dans window au lieu des références locales
    if (window.currentCloseDropdownHandler) {
      document.removeEventListener('click', window.currentCloseDropdownHandler);
      window.currentCloseDropdownHandler = null;
    }
    
    if (window.currentDropdownKeydownHandler) {
      document.removeEventListener('keydown', window.currentDropdownKeydownHandler);
      window.currentDropdownKeydownHandler = null;
    }
    
    // S'assurer que l'input est activé
    messageInput.disabled = false;
    messageInput.readOnly = false;
  }

  // Hide suggestions
  hideSuggestions() {
    this.suggestionsElement.style.display = 'none';
  }

  // Handle keyboard navigation for suggestions
  handleKeyDown(event) {
    if (this.suggestionsElement.style.display === 'none') return;
    
    const suggestions = this.suggestionsElement.querySelectorAll('.feature-suggestion');
    
    // If ESC key is pressed, hide suggestions
    if (event.key === 'Escape') {
      this.hideSuggestions();
      return;
    }
    
    if (suggestions.length === 0) return;
    
    // Vérifier si nous sommes en mode formulaire spécial (wallet, etc.)
    const messageInput = document.getElementById('messageInput');
    if (messageInput && messageInput.dataset.featureCommand === 'true') {
      // Si nous sommes déjà en mode formulaire spécial, ne pas intercepter les touches
      // pour permettre la saisie normale
      return;
    }
    
    // Navigate with arrow keys
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      
      // Find current selected
      const currentSelected = this.suggestionsElement.querySelector('.selected');
      let nextSelected;
      
      if (!currentSelected) {
        // Select first or last depending on arrow direction
        nextSelected = event.key === 'ArrowDown' ? suggestions[0] : suggestions[suggestions.length - 1];
      } else {
        // Find current index
        const currentIndex = Array.from(suggestions).indexOf(currentSelected);
        
        if (event.key === 'ArrowDown') {
          nextSelected = currentIndex < suggestions.length - 1 ? 
            suggestions[currentIndex + 1] : suggestions[0];
        } else {
          nextSelected = currentIndex > 0 ? 
            suggestions[currentIndex - 1] : suggestions[suggestions.length - 1];
        }
        
        currentSelected.classList.remove('selected');
      }
      
      nextSelected.classList.add('selected');
    }
    
    // Fonction commune pour sélectionner une suggestion
    const selectSuggestion = () => {
      const selected = this.suggestionsElement.querySelector('.selected') || 
                      this.suggestionsElement.querySelector('.feature-suggestion');
      
      if (selected) {
        const messageInput = document.getElementById('messageInput');
        const featureName = selected.dataset.feature;
        const triggerText = selected.dataset.trigger;
        
        // Vérifier si cette feature nécessite un formulaire spécial
        const feature = this.features.find(f => f.name === featureName);
        if (feature && (
            (feature.name.includes('wallet') && 
            (feature.name.includes('add') || feature.name.includes('change') || feature.name.includes('scan_specific'))) ||
            feature.name === 'scan_specific_wallet_number'
           )) {
          // Si c'est une feature qui nécessite un formulaire, l'afficher
          this.hideSuggestions();
          this.showWalletForm(feature);
        } else {
          // Sinon, juste insérer le trigger
          messageInput.value = triggerText;
          messageInput.focus();
          this.hideSuggestions();
          
          // Trigger input event for validation
          const inputEvent = new Event('input', { bubbles: true });
          messageInput.dispatchEvent(inputEvent);
        }
        return true;
      }
      return false;
    };
    
    // Handle TAB or Enter to select suggestions
    if (event.key === 'Tab' && this.suggestionsElement.style.display === 'block') {
      event.preventDefault();
      selectSuggestion();
      return;
    }
    
    // Pour la touche Enter, seulement sélectionner une suggestion si l'utilisateur en a
    // explicitement choisi une avec les flèches, sinon laisser le comportement normal (envoi)
    if (event.key === 'Enter' && !event.shiftKey && this.suggestionsElement.style.display === 'block') {
      const hasExplicitSelection = !!this.suggestionsElement.querySelector('.selected');
      
      if (hasExplicitSelection) {
        event.preventDefault();
        event.stopPropagation();
        selectSuggestion();
      } else {
        // L'utilisateur n'a pas explicitement sélectionné de suggestion
        // Cacher les suggestions et laisser le comportement normal d'envoi
        this.hideSuggestions();
      }
      return;
    }
  }
  
  // Set up event listener for send button to hide suggestions
  setupSendButtonListener() {
    const sendBtn = document.getElementById('sendBtn');
    if (sendBtn) {
      sendBtn.addEventListener('click', () => {
        this.hideSuggestions();
      });
    }
  }

  // Process message to check for features
  processMessage(message) {
    const originalMessage = message.trim(); // Conserver le message original
    const lowerCaseMessage = originalMessage.toLowerCase(); // Pour la comparaison des triggers
    
    const userPermission = this.getUserPermissionLevel();
    
    // D'abord, essayer de trouver une correspondance exacte
    for (const feature of this.features) {
      if (!this.checkPermission(feature.permissionLevel, userPermission)) {
        continue;
      }
      
      for (const trigger of feature.trigger) {
        const lowerCaseTrigger = trigger.toLowerCase();
        
        // Vérifier la correspondance exacte
        if (lowerCaseMessage === lowerCaseTrigger) {
          return {
            feature,
            params: ''
          };
        }
      }
    }
    
    // Ensuite, essayer de trouver une correspondance de préfixe (pour les commandes avec paramètres)
    for (const feature of this.features) {
      if (!this.checkPermission(feature.permissionLevel, userPermission)) continue;
      
      for (const trigger of feature.trigger) {
        const lowerCaseTrigger = trigger.toLowerCase();
        
        // Cas spécial pour scan_specific_wallet_number - vérifier si le message contient un numéro après "scan specific wallet" ou "scan wallet number"
        if (feature.name === 'scan_specific_wallet_number') {
          const scanSpecificWalletRegex = /^scan specific wallet (\d+)$/i;
          const scanWalletNumberRegex = /^scan wallet number (\d+)$/i;
          
          const matchSpecificWallet = lowerCaseMessage.match(scanSpecificWalletRegex);
          const matchWalletNumber = lowerCaseMessage.match(scanWalletNumberRegex);
          
          if (matchSpecificWallet || matchWalletNumber) {
            const walletNumber = matchSpecificWallet ? matchSpecificWallet[1] : matchWalletNumber[1];
            return {
              feature,
              params: walletNumber
            };
          }
          
          // Continuer avec les autres features si pas de correspondance
          continue;
        }
        
        // Vérifier si le message commence par le trigger suivi d'un espace
        // Cela assure que "scan my wallets" ne correspondra pas à "scan my wallet"
        if (lowerCaseMessage.startsWith(lowerCaseTrigger + ' ')) {
          // Extraire les paramètres depuis le message ORIGINAL
          return {
            feature,
            params: originalMessage.substring(trigger.length).trim()
          };
        }
        
        // Si le message est exactement le trigger avec rien après
        if (lowerCaseMessage === lowerCaseTrigger) {
     
          return {
            feature,
            params: ''
          };
        }
      }
    }
    
    // Vérifier si le message correspond à une feature pour laquelle l'utilisateur n'a pas la permission
    for (const feature of this.features) {
      // Sauter les features pour lesquelles l'utilisateur a déjà la permission (déjà vérifiées au-dessus)
      if (this.checkPermission(feature.permissionLevel, userPermission)) continue;
      
      for (const trigger of feature.trigger) {
        const lowerCaseTrigger = trigger.toLowerCase();
        
        // Vérifier la correspondance exacte ou avec paramètres pour les permissions
        if (lowerCaseMessage === lowerCaseTrigger || 
            lowerCaseMessage.startsWith(lowerCaseTrigger + ' ')) {
          // L'utilisateur a tenté d'utiliser une feature pour laquelle il n'a pas la permission
          const requiredTokens = feature.permissionLevel === 'lane' ? '1' : '35,000';
          
          return {
            feature: {
              name: 'permission_denied',
              handler: () => {
                return {
                  type: 'error',
                  content: `This feature requires at least ${requiredTokens} LANE tokens. Please acquire more LANE tokens to access this feature.`
                };
              }
            },
            params: ''
          };
        }
      }
    }
    
    // Si aucune feature ne correspond, retourner null
    return null;
  }
  
  // Check if user has permission for a feature
  checkPermission(featureLevel, userLevel) {
    const levels = ['free', 'lane', 'premium'];
    const featureIndex = levels.indexOf(featureLevel);
    const userIndex = levels.indexOf(userLevel);
    
    return userIndex >= featureIndex;
  }

  // Get stored wallets - Use injected store
  getStoredWallets() {
    // Check if store is available and user is connected
    if (!this.store) {
        console.warn("Store not available in getStoredWallets");
        return [];
    }
    const walletAddress = this.store.getWalletAddress();
    if (!walletAddress) {
        // No connected wallet, return empty array (or handle guest mode differently if needed)
        return [];
    }

    // Use a dynamic key based on the connected wallet address
    const storageKey = `quantumWallets_${walletAddress}`;
    try {
      const walletsData = localStorage.getItem(storageKey);
      return walletsData ? JSON.parse(walletsData) : [];
    } catch (e) {
      console.error(`Error parsing stored wallets for ${walletAddress}`, e);
      return [];
    }
  }

  // Save wallets to store - Use injected store
  saveWallets(wallets) {
     // Check if store is available and user is connected
     if (!this.store) {
        console.warn("Store not available in saveWallets");
        return;
     }
     const walletAddress = this.store.getWalletAddress();
     if (!walletAddress) {
        // No connected wallet, cannot save wallets
        console.warn("Cannot save wallets, no connected wallet address.");
        return;
     }

     // Use a dynamic key based on the connected wallet address
     const storageKey = `quantumWallets_${walletAddress}`;
     try {
        localStorage.setItem(storageKey, JSON.stringify(wallets));
     } catch (e) {
        console.error(`Error saving wallets for ${walletAddress}`, e);
        // Optionally notify the user or handle the error
     }
  }

  // Validate Bitcoin address (basic validation)
  validateBitcoinAddress(address) {
    // Make sure we have a string to validate
    if (!address || typeof address !== 'string') {
        return false;
    }
    
    // Trim the address
    address = address.trim();
    
    // Check if address is too short or too long (basic sanity check)
    if (address.length < 26 || address.length > 90) {
         return false;
     }
    
    // Legacy (P2PKH) addresses start with 1
    if (address.startsWith('1')) {
      const isValid = /^1[a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(address);
      return isValid;
    }
    
    // SegWit (P2SH) addresses start with 3
    if (address.startsWith('3')) {
      const isValid = /^3[a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(address);
      return isValid;
    }
    
    // Bech32 (Native SegWit) addresses start with bc1
    if (address.startsWith('bc1')) {
      const isValid = /^bc1[a-z0-9]{39,59}$/.test(address);
      return isValid;
    }
    
    // Taproot addresses start with bc1p
    if (address.startsWith('bc1p')) {
         const isValid = /^bc1p[a-z0-9]{58,59}$/.test(address);
        return isValid;
    }
    
    // If we get here, it doesn't match any known Bitcoin address format
    return false;
  }

  // Detect Bitcoin address type
  detectBitcoinAddressType(address) {
    if (address.startsWith('1')) {
      return 'legacy'; // P2PKH
    } else if (address.startsWith('3')) {
      return 'segwit'; // P2SH
    } else if (address.startsWith('bc1')) {
      return 'bech32'; // Native SegWit
    }
    return null;
  }

  // Normalize network name for backend
  normalizeNetwork(network) {
    network = network.toLowerCase().trim();
    
    // Map user-friendly names to backend expected formats
    const networkMap = {
      'btc': 'bitcoin',
      'bitcoin': 'bitcoin',
      'bitcoincash': 'bitcoin_cash',
      'bch': 'bitcoin_cash',
      'litecoin': 'litecoin',
      'ltc': 'litecoin',
      'ethereum': 'ethereum',
      'eth': 'ethereum',
      'dogecoin': 'dogecoin',
      'doge': 'dogecoin'
    };
    
    return networkMap[network] || 'bitcoin'; // Default to bitcoin if unknown
  }

  // Parse user input for wallet address and network
  parseWalletInput(input) {
    let address = input.trim();
    
    // Pour la commande "change wallet", extraire le numéro de wallet et l'adresse
    let walletNumber = null;
    if (input.toLowerCase().includes('change wallet')) {
      const toIndex = input.toUpperCase().indexOf(' TO ');
      if (toIndex !== -1) {
        // Extract wallet number
        const numberPart = input.substring('change wallet '.length, toIndex).trim();
        if (/^\d+$/.test(numberPart)) {
          walletNumber = parseInt(numberPart);
        }
        
        // Extract the address after "TO" and before any "on/for network" part
        address = input.substring(toIndex + 4).trim();
        
        // If we have a network part, it will be handled by the network regex below
      }
    }
    
    // Check if input contains "on" or "for" followed by network name
    // Examples: "add wallet 1ABC... on bitcoin" or "add wallet 1ABC... for BTC"
    const networkRegex = /\s+(?:on|for)\s+([a-zA-Z0-9_]+)$/i;
    const networkMatch = address.match(networkRegex);
    
    let network = 'bitcoin'; // Default
    
    if (networkMatch) {
      // Extract network and remove it from the address part
      network = this.normalizeNetwork(networkMatch[1]);
      address = address.substring(0, address.length - networkMatch[0].length).trim();
    }
    
    // Si on a un walletNumber, on a déjà extrait l'adresse correctement
    if (!walletNumber) {
      // Clean up the address by removing any additional text at the beginning
      // Find the first character that could start a crypto address
      const addressStartRegex = /[13bc][a-zA-Z0-9]/;
      const addressStartMatch = address.match(addressStartRegex);
      
      if (addressStartMatch) {
        const startIndex = address.indexOf(addressStartMatch[0]);
        address = address.substring(startIndex);
      }
    }
    
    return { network, address, walletNumber };
  }

  // Feature handler: Scan my wallet
  async scanMyWallet(params) {
    // Ensure a conversation exists first
    const conversationCheck = await this.ensureConversationExists();
    if (!conversationCheck.success) {
      return {
        type: 'error',
        content: `Error: ${conversationCheck.error || "Unable to create conversation. Please reload and try again."}`
      };
    }

    const wallets = this.getStoredWallets();
    
    if (wallets.length === 0) {
      return {
        type: 'text',
        content: "You don't have any registered wallets yet. Would you like to add one? Type 'add my wallet' to get started."
      };
    }
    
    try {
      // Get current auth token
      const authToken = localStorage.getItem("quantumAuthToken");
      if (!authToken) {
        throw new Error("Authentication token not found. Please reconnect.");
      }
      
      // Target first wallet
      const wallet = wallets[0];
      const normalizedNetwork = this.normalizeNetwork(wallet.network);
      
      // Prepare request body for the new endpoint
      const requestBody = {
        addresses: [
          {
            addr: wallet.address,
            network: normalizedNetwork
          }
        ],
        mode: "basic"
      };
      
      // Make the API call to the backend
      const response = await fetch(`${apiUrl}/scan-wallet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        // Try to get error message from response
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          // If we can't parse JSON, use status text
          throw new Error(`Failed to scan wallet: ${response.status} ${response.statusText}`);
        }
        
        throw new Error(errorData.error || 'Failed to scan wallet');
      }
      
      // Parse the response
      const data = await response.json();
      
      // Get results for this wallet address
      if (data && data.results && data.results[wallet.address]) {
        return {
          type: 'scan_result',
          wallet: wallet,
          content: data.results[wallet.address]
        };
      } else {
        throw new Error('No data returned for this wallet address');
      }
    } catch (error) {
      return {
        type: 'error',
        content: `Error scanning wallet: ${error.message}`
      };
    }
  }

  // Feature handler: Add wallet
  async addWallet(params) {
    // Ensure a conversation exists first
    const conversationCheck = await this.ensureConversationExists();
    if (!conversationCheck.success) {
      return {
        type: 'error',
        content: `Error: ${conversationCheck.error || "Unable to create conversation. Please reload and try again."}`
      };
    }

    const wallets = this.getStoredWallets();
    const maxWallets = this.getMaxWallets();
    
    if (wallets.length >= maxWallets) {
      return {
        type: 'text',
        content: `You've reached your maximum limit of ${maxWallets} registered wallets. With more LANE tokens, you can register additional wallets.`
      };
    }
    
    // Pour les utilisateurs free, vérifier la restriction de temps
    const userLevel = this.getUserPermissionLevel();
    if (userLevel === 'free' && !this.canAddNewWallet()) {
      const remainingTime = this.getRemainingTimeForWallet();
      const formattedTime = this.formatRemainingTime(remainingTime);
      
      return {
        type: 'error',
        content: `As a free user, you need to wait 24 hours between wallet additions. You can add a new wallet in ${formattedTime}.`
      };
    }
    
    // Parse input to extract address and network
    const { address, network } = this.parseWalletInput(params);
    
    if (!address || address.trim() === '') {
      return {
        type: 'request_info',
        content: "Please provide a Bitcoin wallet address to add. For example: 'add my wallet 1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa on BTC'"
      };
    }
    
    // Validate address based on network
    if (network === 'bitcoin' && !this.validateBitcoinAddress(address)) {
      return {
        type: 'error',
        content: "The address you provided doesn't appear to be a valid Bitcoin address. Please check and try again."
      };
    }
    
    // Auto-detect Bitcoin address type and suggest proper network if mismatch
    if (network === 'bitcoin') {
      const detectedType = this.detectBitcoinAddressType(address);
      if (detectedType) {
        // Everything looks good, proceed with Bitcoin address
      } else {
        return {
          type: 'error',
          content: "The address format doesn't match the Bitcoin network. Please verify the address or specify the correct network."
        };
      }
    }
    
    // Add the wallet with normalized network value and ajout timestamp
    wallets.push({
      address,
      network: this.normalizeNetwork(network),
      label: `Wallet ${wallets.length + 1}`,
      addedAt: Date.now() // Ajouter un timestamp de création
    });
    
    this.saveWallets(wallets);
    
    // Enregistrer l'action d'ajout pour les utilisateurs free
    if (userLevel === 'free') {
      this.recordWalletAddition();
    }
    
    return {
      type: 'success',
      content: `Successfully added your ${network} wallet address. You can now scan it by typing 'scan my wallet'.`
    };
  }

  // Feature handler: Change wallet
  async changeWallet(params) {
    // Ensure a conversation exists first
    const conversationCheck = await this.ensureConversationExists();
    if (!conversationCheck.success) {
      return {
        type: 'error',
        content: `Error: ${conversationCheck.error || "Unable to create conversation. Please reload and try again."}`
      };
    }

    const wallets = this.getStoredWallets();
    
    if (wallets.length === 0) {
      return {
        type: 'text',
        content: "You don't have any registered wallets yet. Type 'add my wallet' to add one."
      };
    }
    
    // Parse input to extract address and network
    const { address, network, walletNumber } = this.parseWalletInput(params);
    
    // Vérifier si un numéro de wallet a été spécifié
    let walletIndex = -1;
    
    if (walletNumber !== null && walletNumber > 0) {
      walletIndex = walletNumber - 1; // Convertir en index basé sur 0
    } else {
      // L'ancienne logique pour extraire le numéro du premier mot (pour compatibilité)
      const firstWord = params.split(/\s+/)[0];
      let remainingParams = params;
      
      if (/^\d+$/.test(firstWord)) {
        walletIndex = parseInt(firstWord) - 1; // Convert to 0-based index
        remainingParams = params.substring(firstWord.length).trim();
      } else {
        walletIndex = 0; // Default to first wallet if no index specified
      }
    }
    
    // Check if index is valid
    if (walletIndex < 0 || walletIndex >= wallets.length) {
      return {
        type: 'error',
        content: `You only have ${wallets.length} registered wallet(s). Please provide a valid wallet number.`
      };
    }
    
    // Vérifier les restrictions de modification
    const userLevel = this.getUserPermissionLevel();
    if (!this.canModifyWallet(walletIndex)) {
      if (userLevel !== 'free') {
        return {
          type: 'error',
          content: `As a LANE holder, you can only change each wallet once per day. Please wait 24 hours since your last change.`
        };
      } else {
        // Pour les utilisateurs free, vérifier les deux cas:
        // 1. Moins de 24h après l'ajout initial
        // 2. Moins de 24h après une modification
        const wallet = wallets[walletIndex];
        
        if (wallet.addedAt) {
          const sinceAddition = Date.now() - wallet.addedAt;
          const twentyFourHoursInMs = 24 * 60 * 60 * 1000;
          
          if (sinceAddition < twentyFourHoursInMs) {
            const remainingTime = twentyFourHoursInMs - sinceAddition;
            const formattedTime = this.formatRemainingTime(remainingTime);
            
            return {
              type: 'error',
              content: `As a free user, you need to wait 24 hours after adding a wallet before changing it. You can modify this wallet in ${formattedTime}.`
            };
          }
        }
        
        // Si ce n'est pas un problème d'ajout récent, c'est une modification récente
        const remainingTime = this.getRemainingTimeForWallet(walletIndex);
        const formattedTime = this.formatRemainingTime(remainingTime);
        
        return {
          type: 'error',
          content: `As a free user, you need to wait 24 hours between wallet changes. You can modify this wallet in ${formattedTime}.`
        };
      }
    }
    
    // Validate address based on network
    if (network === 'bitcoin' && !this.validateBitcoinAddress(address)) {
      return {
        type: 'error',
        content: "The address you provided doesn't appear to be a valid Bitcoin address. Please check and try again."
      };
    }
    
    // Auto-detect Bitcoin address type and suggest proper network if mismatch
    if (network === 'bitcoin') {
      const detectedType = this.detectBitcoinAddressType(address);
      if (!detectedType) {
        return {
          type: 'error',
          content: "The address format doesn't match the Bitcoin network. Please verify the address or specify the correct network."
        };
      }
    }
    
    // Update the wallet with normalized network
    wallets[walletIndex].address = address;
    wallets[walletIndex].network = this.normalizeNetwork(network);
    this.saveWallets(wallets);
    
    // Enregistrer la modification
    this.recordWalletChange(walletIndex);
    
    return {
      type: 'success',
      content: `Successfully updated wallet ${walletIndex + 1} with the new ${network} address.`
    };
  }

  // Feature handler: Scan specific wallet number
  async scanMyWalletNumber(params) {
    // Ensure a conversation exists first
    const conversationCheck = await this.ensureConversationExists();
    if (!conversationCheck.success) {
      return {
        type: 'error',
        content: `Error: ${conversationCheck.error || "Unable to create conversation. Please reload and try again."}`
      };
    }

    if (this.getUserPermissionLevel() === 'free') {
      return {
        type: 'error',
        content: "This feature requires LANE tokens. Please acquire LANE tokens to access this feature."
      };
    }
    
    const wallets = this.getStoredWallets();
    
    if (wallets.length === 0) {
      return {
        type: 'text',
        content: "You don't have any registered wallets yet. Type 'add my wallet' to add one."
      };
    }
    
    // Extract wallet number
    
    // Le numéro de wallet pourrait déjà être directement dans les params
    let walletNumber = parseInt(params);
    
    // Si params n'est pas un numéro, essayons d'extraire un numéro
    if (isNaN(walletNumber)) {
      const match = params.match(/\d+/);
      if (!match) {
        return {
          type: 'request_info',
          content: `Please specify which wallet to scan (1-${wallets.length}).`
        };
      }
      walletNumber = parseInt(match[0]);
    }
    
    // Adjust for 0-based index
    const walletIndex = walletNumber - 1;
    
    if (walletIndex < 0 || walletIndex >= wallets.length) {
      return {
        type: 'error',
        content: `Invalid wallet number. You have ${wallets.length} registered wallet(s). Please choose a number between 1 and ${wallets.length}.`
      };
    }
    
    try {
      // Get current auth token
      const authToken = localStorage.getItem("quantumAuthToken");
      if (!authToken) {
        throw new Error("Authentication token not found. Please reconnect.");
      }
      
      // Get the specified wallet
      const wallet = wallets[walletIndex];
      
      const normalizedNetwork = this.normalizeNetwork(wallet.network);
      
      // Prepare request body for the new endpoint
      const requestBody = {
        addresses: [
          {
            addr: wallet.address,
            network: normalizedNetwork
          }
        ],
        mode: "basic"
      };
      
      // Make the API call to the backend
      const response = await fetch(`${apiUrl}/scan-wallet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        // Try to get error message from response
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          // If we can't parse JSON, use status text
          throw new Error(`Failed to scan wallet: ${response.status} ${response.statusText}`);
        }
        
        throw new Error(errorData.error || 'Failed to scan wallet');
      }
      
      // Parse the response
      const data = await response.json();
      
      // Get results for this wallet address
      if (data && data.results && data.results[wallet.address]) {
        return {
          type: 'scan_result',
          wallet: wallet,
          content: data.results[wallet.address]
        };
      } else {
        throw new Error('No data returned for this wallet address');
      }
   
    } catch (error) {
      return {
        type: 'error',
        content: `Error scanning wallet: ${error.message}`
      };
    }
  }

  // Feature handler: Scan all wallets
  async scanAllWallets() {
    // Ensure a conversation exists first
    const conversationCheck = await this.ensureConversationExists();
    if (!conversationCheck.success) {
      return {
        type: 'error',
        content: `Error: ${conversationCheck.error || "Unable to create conversation. Please reload and try again."}`
      };
    }

    if (this.getUserPermissionLevel() === 'free') {
      return {
        type: 'error',
        content: "This feature requires LANE tokens. Please acquire LANE tokens to access this feature."
      };
    }
    
    const wallets = this.getStoredWallets();
    
    if (wallets.length === 0) {
      return {
        type: 'text',
        content: "You don't have any registered wallets yet. Type 'add my wallet' to add one."
      };
    }
    
    try {
      // Get current auth token
      const authToken = localStorage.getItem("quantumAuthToken");
      if (!authToken) {
        throw new Error("Authentication token not found. Please reconnect.");
      }
      
      // Prepare the addresses array for the API request
      const addresses = wallets.map(wallet => ({
        addr: wallet.address,
        network: this.normalizeNetwork(wallet.network)
      }));
      
      
      // Make the API call to scan all wallets at once
      const response = await fetch(`${apiUrl}/scan-wallet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          addresses: addresses,
          mode: "basic"
        })
      });
      
      if (!response.ok) {
        // Try to get error message from response
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          throw new Error(`Failed to scan wallets: ${response.status} ${response.statusText}`);
        }
        
        throw new Error(errorData.error || 'Failed to scan wallets');
      }
      
      // Parse the API response
      const data = await response.json();
      
      // Transform the response to match our expected format
      const results = wallets.map(wallet => {
        const walletData = data.results && data.results[wallet.address];
        
        if (walletData) {
          return { wallet, result: walletData };
        } else {
          return { wallet, error: 'No data returned for this wallet address' };
        }
      });
      
      return {
        type: 'multiple_scan_results',
        content: results,
        summary: data.summary // Include the overall summary in the response
      };
    } catch (error) {
      console.error("Error scanning all wallets:", error);
      return {
        type: 'error',
        content: `Error scanning wallets: ${error.message}`
      };
    }
  }

  // Feature handler: Scan specific wallet address
  async scanSpecificWallet(params) {
    // Ensure a conversation exists first
    const conversationCheck = await this.ensureConversationExists();
    if (!conversationCheck.success) {
      return {
        type: 'error',
        content: `Error: ${conversationCheck.error || "Unable to create conversation. Please reload and try again."}`
      };
    }

    if (this.getUserPermissionLevel() !== 'premium') {
      return {
        type: 'error',
        content: "This premium feature requires at least 35,000 LANE tokens. Please acquire more LANE tokens to access this feature."
      };
    }
    
    // Parse input to extract address and network
    const { address, network } = this.parseWalletInput(params);
    
    if (!address || address.trim() === '') {
      return {
        type: 'request_info',
        content: "Please provide a wallet address to scan. For example: 'scan the wallet 1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa on BTC'"
      };
    }
    
    // Validate address based on network
    if (network === 'bitcoin' && !this.validateBitcoinAddress(address)) {
      return {
        type: 'error',
        content: "The address you provided doesn't appear to be a valid Bitcoin address. Please check and try again."
      };
    }
    
    // Auto-detect Bitcoin address type and suggest proper network if mismatch
    if (network === 'bitcoin') {
      const detectedType = this.detectBitcoinAddressType(address);
      if (!detectedType) {
        return {
          type: 'error',
          content: "The address format doesn't match the Bitcoin network. Please verify the address or specify the correct network."
        };
      }
    }
    
    try {
      // Get current auth token
      const authToken = localStorage.getItem("quantumAuthToken");
      if (!authToken) {
        throw new Error("Authentication token not found. Please reconnect.");
      }
      
      // Normalize network
      const normalizedNetwork = this.normalizeNetwork(network);
      
      // Prepare request body for the new endpoint
      const requestBody = {
        addresses: [
          {
            addr: address,
            network: normalizedNetwork
          }
        ],
        mode: "basic"
      };
      
      // Make the API call to the backend
      const response = await fetch(`${apiUrl}/scan-wallet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        // Try to get error message from response
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          // If we can't parse JSON, use status text
          throw new Error(`Failed to scan wallet: ${response.status} ${response.statusText}`);
        }
        
        throw new Error(errorData.error || 'Failed to scan wallet');
      }
      
      // Parse and return the response
      const data = await response.json();
      
      // Transform the response to match the expected format in our UI
      // For a single address, we return the first result
      if (data && data.results && data.results[address]) {
        return {
          type: 'scan_result',
          wallet: { 
            address, 
            network: normalizedNetwork,
            label: `${normalizedNetwork} Address`
          },
          content: data.results[address]
        };
      } else {
        throw new Error('No data returned for this wallet address');
      }
    } catch (error) {
      return {
        type: 'error',
        content: `Error scanning wallet: ${error.message}`
      };
    }
  }

  // Call backend to scan wallet (placeholder)
  async callBackendScan(address, network) {
    // Normalize network before sending to backend
    network = this.normalizeNetwork(network);
    
    try {
      // Get current auth token
      const authToken = localStorage.getItem("quantumAuthToken");
      if (!authToken) {
        throw new Error("Authentication token not found. Please reconnect.");
      }
      
      // Format the request body for the new endpoint
      const requestBody = {
        addresses: [
          {
            addr: address,
            network: network
          }
        ],
        mode: "basic"
      };
      
      // Make the API call to the backend
      const response = await fetch(`${apiUrl}/scan-wallet`, { 
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        // Try to get error message from response
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          // If we can't parse JSON, use status text
          throw new Error(`Failed to scan wallet: ${response.status} ${response.statusText}`);
        }
        
        throw new Error(errorData.error || 'Failed to scan wallet');
      }
      
      // Parse and return the response
      const data = await response.json();
      
      // Transform the response to match the expected format in our UI
      // For a single address, we return the first result
      if (data && data.results && data.results[address]) {
        return data.results[address];
      } else {
        throw new Error('No data returned for this wallet address');
      }
    } catch (error) {
      console.error('Error scanning wallet:', error);
      throw error;
    }
  }

  // Ensure a conversation exists before processing features - USE INJECTED DEPENDENCIES
  async ensureConversationExists() {
    // Check if dependencies are initialized
    if (!this.store || !this.createNewConversation) {
       console.error("FeatureSystem not initialized. Store or createNewConversation is missing.");
       // Attempt to gracefully fallback or return error
       // Check window as a last resort (though not ideal)
        if (typeof window.store?.getCurrentConversationId === 'function' && typeof window.createNewConversation === 'function') {
             console.warn("FeatureSystem falling back to window objects.");
             this.store = window.store;
             this.createNewConversation = window.createNewConversation;
        } else {
           return {
            conversationId: null,
            success: false,
            error: "Feature system not properly initialized."
           };
        }
    }

    try {
      
      let currentConversationId = this.store.getCurrentConversationId();
      
      if (!currentConversationId) {
        
        // Use the injected function
        currentConversationId = this.createNewConversation(); 
        
        // The createNewConversation function is now responsible 
        // for updating the store and setting the current ID.
        // We assume it does this reliably.
        
        if (!currentConversationId) {
           // If createNewConversation failed to return an ID
           console.error("Injected createNewConversation did not return an ID.");
           throw new Error("Failed to create conversation via injected function");
        }

         
        // Optional: Brief delay to allow potential async updates in store
        await new Promise(resolve => setTimeout(resolve, 50)); 

        // Verify it's set in the store now
        const checkId = this.store.getCurrentConversationId();
        if(checkId !== currentConversationId) {
             console.warn("Store's current ID doesn't match newly created ID immediately. Relying on createNewConversation's logic.");
             // Potentially force set it if createNewConversation is known not to
             // this.store.setCurrentConversationId(currentConversationId); 
        }

         // Verify the conversation exists in the list
         const conversations = this.store.getConversations();
         const conversationExists = conversations.some(c => c.id === currentConversationId);

         if (!conversationExists) {
           console.error("Failed to find conversation in store immediately after creation.");
           // Consider a slightly longer delay or re-fetch if necessary
           await new Promise(resolve => setTimeout(resolve, 200));
           const updatedConversations = this.store.getConversations();
            if (!updatedConversations.some(c => c.id === currentConversationId)) {
               throw new Error("Failed to confirm conversation creation in store.");
            }
         } else {
         }
      }
      
      return {
        conversationId: currentConversationId,
        success: true
      };
    } catch (error) {
      console.error("Error ensuring conversation exists:", error);
      return {
        conversationId: null,
        success: false,
        error: error.message || "Unknown error creating/verifying conversation"
      };
    }
  }

  // Méthode pour vérifier si un utilisateur peut modifier un wallet
  canModifyWallet(walletIndex) {
    const userLevel = this.getUserPermissionLevel();
    
    // Les utilisateurs avec des tokens $LANE peuvent faire 1 changement par wallet par jour
    if (userLevel !== 'free') {
      // Récupérer l'historique des modifications de wallet
      const walletHistory = this.getWalletChangeHistory();
      
      // Vérifier si ce wallet a été modifié dans les dernières 24h
      const lastChange = walletHistory.changes.find(change => change.walletIndex === walletIndex);
      if (!lastChange) return true; // Pas encore de modification
      
      // Vérifier si 24h se sont écoulées depuis la dernière modification
      const twentyFourHoursInMs = 24 * 60 * 60 * 1000;
      return (Date.now() - lastChange.timestamp) >= twentyFourHoursInMs;
    }
    
    // Pour les utilisateurs gratuits, vérifier le délai de 24h
    const walletHistory = this.getWalletChangeHistory();
    
    // On récupère les wallets pour connaître leur date d'ajout (timestamp)
    const wallets = this.getStoredWallets();
    const wallet = wallets[walletIndex];
    
    if (!wallet || !wallet.addedAt) {
      // Si le wallet n'a pas de date d'ajout, on l'ajoute maintenant
      if (wallet) {
        wallets[walletIndex].addedAt = Date.now();
        this.saveWallets(wallets);
      }
      return false; // Par sécurité, exiger 24h
    }
    
    // Vérifier si 24h se sont écoulées depuis l'ajout du wallet
    const twentyFourHoursInMs = 24 * 60 * 60 * 1000;
    const sinceAddition = Date.now() - wallet.addedAt;
    
    // Si moins de 24h depuis l'ajout, bloquer la modification
    if (sinceAddition < twentyFourHoursInMs) {
      return false;
    }
    
    // Vérifier également s'il y a eu des modifications récentes
    const lastChange = walletHistory.changes.find(change => change.walletIndex === walletIndex);
    
    if (!lastChange) return true; // Pas encore de modification après l'ajout initial
    
    // Vérifier si 24h se sont écoulées depuis la dernière modification
    return (Date.now() - lastChange.timestamp) >= twentyFourHoursInMs;
  }
  
  // Méthode pour vérifier si un utilisateur peut ajouter un nouveau wallet
  canAddNewWallet() {
    const userLevel = this.getUserPermissionLevel();
    
    // Les utilisateurs avec des tokens $LANE peuvent ajouter sans restriction
    if (userLevel !== 'free') return true;
    
    // Pour les utilisateurs gratuits, vérifier s'ils ont ajouté un wallet récemment
    const walletHistory = this.getWalletChangeHistory();
    
    if (!walletHistory.lastAddition) return true; // Pas encore d'ajout
    
    // Vérifier si 24h se sont écoulées depuis le dernier ajout
    const twentyFourHoursInMs = 24 * 60 * 60 * 1000;
    return (Date.now() - walletHistory.lastAddition) >= twentyFourHoursInMs;
  }
  
  // Méthode pour enregistrer une modification de wallet
  recordWalletChange(walletIndex) {
    // Check if store is available and user is connected
    if (!this.store) {
        console.warn("Store not available in recordWalletChange");
        return;
    }
    const walletAddress = this.store.getWalletAddress();
    if (!walletAddress) {
        console.warn("Cannot record wallet change, no connected wallet address.");
        return;
    }

    const walletHistory = this.getWalletChangeHistory(); // Already uses address-specific key

    // Chercher si ce wallet a déjà été modifié
    const existingChangeIndex = walletHistory.changes.findIndex(
      change => change.walletIndex === walletIndex
    );
    
    if (existingChangeIndex !== -1) {
      // Mettre à jour le timestamp
      walletHistory.changes[existingChangeIndex].timestamp = Date.now();
    } else {
      // Ajouter une nouvelle entrée
      walletHistory.changes.push({
        walletIndex,
        timestamp: Date.now()
      });
    }
    
    // Enregistrer l'historique using the dynamic key
    const historyKey = `walletHistory_${walletAddress}`;
    try {
        localStorage.setItem(historyKey, JSON.stringify(walletHistory));
    } catch (e) {
        console.error(`Error saving wallet change history for ${walletAddress}`, e);
    }
  }
  
  // Méthode pour enregistrer un ajout de wallet
  recordWalletAddition() {
    // Check if store is available and user is connected
    if (!this.store) {
        console.warn("Store not available in recordWalletAddition");
        return;
    }
    const walletAddress = this.store.getWalletAddress();
    if (!walletAddress) {
        console.warn("Cannot record wallet addition, no connected wallet address.");
        return;
    }

    const walletHistory = this.getWalletChangeHistory(); // Already uses address-specific key
    walletHistory.lastAddition = Date.now();

    // Enregistrer l'historique using the dynamic key
    const historyKey = `walletHistory_${walletAddress}`;
    try {
        localStorage.setItem(historyKey, JSON.stringify(walletHistory));
    } catch (e) {
        console.error(`Error saving wallet addition history for ${walletAddress}`, e);
    }
  }
  
  // Méthode pour récupérer l'historique des modifications de wallet
  getWalletChangeHistory() {
    // Check if store is available and user is connected
    const defaultHistory = { changes: [], lastAddition: null };
    if (!this.store) {
        console.warn("Store not available in getWalletChangeHistory");
        return defaultHistory;
    }
    const walletAddress = this.store.getWalletAddress();
    if (!walletAddress) {
        // No connected wallet, return default history
        return defaultHistory;
    }

    // Use a dynamic key based on the connected wallet address
    const historyKey = `walletHistory_${walletAddress}`;
    try {
      const historyData = localStorage.getItem(historyKey);
      if (historyData) {
        return JSON.parse(historyData);
      }
    } catch (e) {
      console.error(`Error parsing wallet change history for ${walletAddress}`, e);
    }

    // Par défaut, retourner un historique vide
    return defaultHistory;
  }
  
  // Méthode pour calculer le temps restant avant de pouvoir modifier/ajouter
  getRemainingTimeForWallet(walletIndex = null) {
    const walletHistory = this.getWalletChangeHistory();
    const twentyFourHoursInMs = 24 * 60 * 60 * 1000;
    
    if (walletIndex !== null) {
      // Cas de modification d'un wallet spécifique
      const lastChange = walletHistory.changes.find(change => change.walletIndex === walletIndex);
      if (!lastChange) return 0;
      
      const elapsedTime = Date.now() - lastChange.timestamp;
      return Math.max(0, twentyFourHoursInMs - elapsedTime);
    } else {
      // Cas d'ajout d'un nouveau wallet
      if (!walletHistory.lastAddition) return 0;
      
      const elapsedTime = Date.now() - walletHistory.lastAddition;
      return Math.max(0, twentyFourHoursInMs - elapsedTime);
    }
  }
  
  // Méthode pour formater le temps restant en heures et minutes
  formatRemainingTime(remainingTimeMs) {
    if (remainingTimeMs <= 0) return "now";
    
    const hours = Math.floor(remainingTimeMs / (60 * 60 * 1000));
    const minutes = Math.floor((remainingTimeMs % (60 * 60 * 1000)) / (60 * 1000));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes} minutes`;
    }
  }

  // Méthode pour assurer que les wallets existants ont une date d'ajout
  // (pour la migration des données existantes)
  ensureWalletTimestamps() {
    const wallets = this.getStoredWallets();
    let needsUpdate = false;
    
    wallets.forEach((wallet, index) => {
      if (!wallet.addedAt) {
        wallets[index].addedAt = Date.now() - (24 * 60 * 60 * 1000); // Par défaut, considérer comme ajouté il y a 24h
        needsUpdate = true;
      }
    });
    
    if (needsUpdate) {
      this.saveWallets(wallets);
    }
  }
}

// Export feature system
export const featureSystem = new FeatureSystem(); 

// IMPORTANT: You now need to call featureSystem.initialize(store, createNewConversation) 
// in your main application script AFTER the store is initialized and 
// createNewConversation is defined.

// Example in your main script (e.g., main.js or app.js):
/*
import store from './store.js';
import { featureSystem } from './features.js';
import { createNewConversation } from './conversations.js'; // Assuming it's exported here

// ... other initializations ...

// Initialize the store first
store.initialize(); // Or however your store is initialized

// Then initialize the feature system with dependencies
featureSystem.initialize(store, createNewConversation);

// ... rest of your application setup ...
*/ 

